// Buyer-Side Diagnostic — the Momentum Radar. A 2×2 scatter (X = Buying Commitment, Y = Belief, the
// buyer's conviction the solution fits) with the four quadrants ADVANCING / WARM-STALLED /
// FALSE-POSITIVE / AT-RISK. The `intent` field name is kept internally as the belief axis. A deal is a
// plotted point; its quadrant is the read. Built for an ARRAY of points so the Phase-2 cohort
// scatter ("pipeline risk view") is additive, not a rewrite.
//
// Hand-rolled inline SVG via createElementNS + textContent (never innerHTML) — the app's no-raw-HTML
// CSP posture. Clean, lean light-theme look: dark-grey rings on the page background, no frame, no
// glow, no animated sweep, so the plotted deal reads first (the CRO wants the coordinate, not a show).

import type { BuyerQuadrant } from '../lib/types.js';

const SVGNS = 'http://www.w3.org/2000/svg';

export interface RadarPoint {
  intent: number; // 0–100  (Y)
  commitment: number; // 0–100  (X)
  quadrant: BuyerQuadrant;
  label?: string;
  primary?: boolean; // the focused deal (single-transcript); gets crosshairs + pulse
}

const QUAD = {
  advancing: { fill: '#2a9d57', label: 'ADVANCING' },
  warm_stalled: { fill: '#c8951a', label: 'WARM · STALLED' },
  false_positive: { fill: '#d98411', label: 'FALSE-POSITIVE' },
  at_risk: { fill: '#d64a41', label: 'AT-RISK' },
} as const;

// Clean, lean light-theme palette: dark-grey rings on the page background (no frame, no glow) with a
// slow light-gold radar sweep, so the plotted deal reads first. Quadrant identity comes from the
// corner labels and the point's own ring colour.
const RING = 'rgba(0,0,0,.14)'; // concentric rings (graphite hairlines on the light canvas)
const GRID = 'rgba(0,0,0,.20)'; // the 50/50 mid-lines
const AXIS = 'rgba(0,0,0,.60)'; // axis + tick text (dark, reads on the light ground)

const ML = 52;
const MR = 16;
const MT = 40;
const MB = 56;
const SWEEP = '#e0a516'; // radar sweep (soft gold instrument sector on the light ground)
const DOT = '#ffbe17'; // deal dot — brand gold (matches the active side-nav item)
const PLOT = 300;
const W = ML + PLOT + MR;
const H = MT + PLOT + MB;

function el(tag: string, attrs: Record<string, string>, text?: string): SVGElement {
  const node = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (text !== undefined) node.textContent = text;
  return node;
}

const px = (commitment: number): number => ML + (Math.max(0, Math.min(100, commitment)) / 100) * PLOT;
const py = (intent: number): number => MT + (1 - Math.max(0, Math.min(100, intent)) / 100) * PLOT;

const HALF = PLOT / 2;
const CX = ML + HALF;
const CY = MT + HALF;

/** Keep a plotted point inside the outer ring. Extreme scores (e.g. belief 0 + commitment 0) land in a
 *  square CORNER, which is outside the inscribed circle — pull those back onto the ring along their own
 *  angle so the dot always sits inside the perimeter, in the right quadrant. */
function clampToRing(x: number, y: number, dotR: number): [number, number] {
  const dx = x - CX;
  const dy = y - CY;
  const dist = Math.hypot(dx, dy);
  const maxDist = HALF - dotR - 2;
  if (dist > maxDist && dist > 0) {
    const k = maxDist / dist;
    return [CX + dx * k, CY + dy * k];
  }
  return [x, y];
}

/** The radar frame (rings + rotating sweep + midlines + quadrant labels + axes), shared by the real
 *  render and the idle-state demo. No frame, no fill, no glow — rings sit on the page background. */
function buildRadarFrame(ariaLabel: string): SVGElement {
  const svg = el('svg', {
    viewBox: `0 0 ${W} ${H}`,
    class: 'br-radar',
    role: 'img',
    'aria-label': ariaLabel,
    preserveAspectRatio: 'xMidYMid meet',
  });

  // Rotating radar sweep — a light-gold sector that stays INSIDE the outer ring (radius < half), so
  // the swept area never spills past it. SMIL rotate, CSP-safe (no inline style).
  const sweepR = HALF * 0.97;
  const a0 = -Math.PI / 2; // 12 o'clock
  const a1 = a0 + (46 * Math.PI) / 180; // ~46° wedge
  const p0 = `${(CX + sweepR * Math.cos(a0)).toFixed(1)} ${(CY + sweepR * Math.sin(a0)).toFixed(1)}`;
  const p1 = `${(CX + sweepR * Math.cos(a1)).toFixed(1)} ${(CY + sweepR * Math.sin(a1)).toFixed(1)}`;
  const sweep = el('path', { d: `M ${CX} ${CY} L ${p0} A ${sweepR} ${sweepR} 0 0 1 ${p1} Z`, fill: SWEEP, opacity: '0.22' });
  sweep.appendChild(el('animateTransform', { attributeName: 'transform', type: 'rotate', from: `0 ${CX} ${CY}`, to: `360 ${CX} ${CY}`, dur: '8s', repeatCount: 'indefinite' }));
  svg.appendChild(sweep);

  // Concentric dark-grey rings.
  for (const r of [HALF * 0.33, HALF * 0.66, HALF]) {
    svg.appendChild(el('circle', { cx: String(CX), cy: String(CY), r: String(r), fill: 'none', stroke: RING, 'stroke-width': '1' }));
  }

  // Mid-lines (the 50/50 split that defines the quadrants).
  svg.appendChild(el('line', { x1: String(CX), y1: String(MT), x2: String(CX), y2: String(MT + PLOT), stroke: GRID, 'stroke-width': '1', 'stroke-dasharray': '3 4' }));
  svg.appendChild(el('line', { x1: String(ML), y1: String(CY), x2: String(ML + PLOT), y2: String(CY), stroke: GRID, 'stroke-width': '1', 'stroke-dasharray': '3 4' }));

  // Quadrant corner labels — each in its own quadrant colour (reads on the light ground), pushed into
  // the extreme corners to clear the ring.
  const qlabel = (x: number, y: number, anchor: string, q: BuyerQuadrant) =>
    el('text', { x: String(x), y: String(y), 'text-anchor': anchor, fill: QUAD[q].fill, 'font-size': '10', 'font-weight': '700', 'letter-spacing': '0.06em' }, QUAD[q].label);
  svg.appendChild(qlabel(ML + PLOT - 2, MT - 4, 'end', 'advancing'));
  svg.appendChild(qlabel(ML + 2, MT - 4, 'start', 'warm_stalled'));
  svg.appendChild(qlabel(ML + PLOT - 2, MT + PLOT + 12, 'end', 'false_positive'));
  svg.appendChild(qlabel(ML + 2, MT + PLOT + 12, 'start', 'at_risk'));

  // Axis labels.
  svg.appendChild(el('text', { x: String(CX), y: String(H - 8), 'text-anchor': 'middle', fill: AXIS, 'font-size': '11', 'font-weight': '700' }, 'Buying Commitment  →'));
  svg.appendChild(el('text', { x: '16', y: String(CY), 'text-anchor': 'middle', fill: AXIS, 'font-size': '11', 'font-weight': '700', transform: `rotate(-90 16 ${CY})` }, 'Belief  →'));
  return svg;
}

/** Idle-state demo: the live radar frame plus a gold dot that blinks 3 times, then jumps to another
 *  random spot across the four quadrants (pure SMIL — discrete cx/cy hops synced to the blink). */
export function renderBuyerRadarDemo(host: HTMLElement): void {
  host.textContent = '';
  const svg = buildRadarFrame('Momentum radar preview: an example deal moving across the four quadrants.');
  const N = 6;
  const HOLD = 4; // seconds each spot is held (= 3 blinks) before hopping — slow, calm blink
  const xs: string[] = [];
  const ys: string[] = [];
  for (let i = 0; i < N; i++) {
    const [x, y] = clampToRing(px(12 + Math.random() * 76), py(12 + Math.random() * 76), 7);
    xs.push(x.toFixed(1));
    ys.push(y.toFixed(1));
  }
  const keyTimes = Array.from({ length: N }, (_, i) => (i / N).toFixed(3)).join(';');
  const dur = `${N * HOLD}s`;
  const dot = el('circle', { cx: xs[0]!, cy: ys[0]!, r: '7', fill: DOT });
  dot.appendChild(el('animate', { attributeName: 'cx', values: xs.join(';'), keyTimes, calcMode: 'discrete', dur, repeatCount: 'indefinite' }));
  dot.appendChild(el('animate', { attributeName: 'cy', values: ys.join(';'), keyTimes, calcMode: 'discrete', dur, repeatCount: 'indefinite' }));
  // 3 blinks per HOLD window, looping — starts and ends visible so the hop happens on a lit dot.
  dot.appendChild(el('animate', { attributeName: 'opacity', values: '1;0.2;1;0.2;1;0.2;1', dur: `${HOLD}s`, repeatCount: 'indefinite' }));
  svg.appendChild(dot);
  host.appendChild(svg);
}

/** Render the radar into `host` (cleared first). Pass one point (single deal) or many (cohort). */
export function renderBuyerRadar(host: HTMLElement, points: RadarPoint[]): void {
  host.textContent = '';
  const svg = buildRadarFrame(radarAria(points));

  // Points. Draw non-primary first so the focused deal sits on top.
  for (const p of [...points].sort((a, b) => Number(a.primary) - Number(b.primary))) {
    const dotR = p.primary ? 7 : 5;
    const [x, y] = clampToRing(px(p.commitment), py(p.intent), dotR);
    // The deal dot: solid brand gold, no coloured outline, with a soft blink so it draws the eye gently.
    const dot = el('circle', { cx: String(x), cy: String(y), r: String(dotR), fill: DOT });
    dot.appendChild(el('animate', { attributeName: 'opacity', values: '1;0.5;1', dur: '1.8s', repeatCount: 'indefinite' }));
    const title = el('title', {}, `${p.label ? p.label + ', ' : ''}${QUAD[p.quadrant].label} · belief ${p.intent}, commitment ${p.commitment}`);
    dot.appendChild(title);
    svg.appendChild(dot);
    if (p.label && points.length > 1) {
      svg.appendChild(el('text', { x: String(x + 9), y: String(y + 3), fill: AXIS, 'font-size': '9' }, p.label.slice(0, 18)));
    }
  }

  host.appendChild(svg);
}

function radarAria(points: RadarPoint[]): string {
  if (!points.length) return 'Momentum radar, no data';
  if (points.length === 1) {
    const p = points[0]!;
    return `Momentum radar: this deal is in the ${QUAD[p.quadrant].label} quadrant, belief ${p.intent} of 100, buying commitment ${p.commitment} of 100.`;
  }
  const counts = points.reduce<Record<string, number>>((m, p) => ((m[p.quadrant] = (m[p.quadrant] ?? 0) + 1), m), {});
  return `Momentum radar with ${points.length} deals. ` + (Object.keys(counts) as BuyerQuadrant[]).map((q) => `${counts[q]} ${QUAD[q].label}`).join(', ') + '.';
}
